import React from 'react';

import debounce from 'debounce-promise';
import { convertToRaw, EditorState, Modifier } from 'draft-js';
import { equals, isNil } from 'ramda';

import { formatWysiwygText } from 'utils/common';

import { useLinkPortal } from '../Toolbar/Items/Link/useLinkPortal';
import { getBlockMapFromText, getInitialState, getTextLengthOfSelectedBlocks } from '../utils';
import * as t from '../Wysiwyg.types';
import { useBlockStyleFn } from './useBlockStyleHandler';
import { useEditorRefs } from './useEditorRefs';
import { useHandleKeyCommand } from './useHandleKeyCommand';
import { useKeyBindingFn } from './useKeyBindingFn';

export const useEditor = (props: t.InnerProps): t.Editor => {
  const [isFocused, setIsFocused] = React.useState(false);
  const [isEditorActive, setIsEditorActive] = React.useState(true);
  const [editorState, setEditorState] = React.useState(() => getInitialState(props));

  const refs = useEditorRefs({
    onOutsideClick: () => {
      if (isFocused) {
        blur();
      }
    },
  });

  const linkPortalController = useLinkPortal();

  const handleKeyCommand = useHandleKeyCommand({
    editorState,
    linkPortalController,
    setEditorState,
    setIsEditorActive,
    handleChange: (editorState: EditorState, forceSelection?: boolean) => {
      handleChange(editorState, forceSelection);
    },
    keyCommandHandler: props.handleKeyCommand,
  });
  const keyBindingFn = useKeyBindingFn({
    editorState: editorState,
    keyBindingHandler: props.handleKeyBinding,
    blur: () => blur(),
  });
  const blockStyleFn = useBlockStyleFn();

  React.useEffect(() => {
    if (!isFocused) {
      setEditorState(getInitialState(props));
    }
  }, [props.data]);

  const emitChange = React.useMemo(
    () =>
      debounce((state: t.OnChangeState) => {
        if (props.onChange) {
          props.onChange(state);
        }
      }, 200),
    [props.onChange],
  );

  const handleChange = (newEditorState: EditorState, forceSelection: boolean = false) => {
    const newContentState = newEditorState.getCurrentContent();
    const currentContentState = editorState.getCurrentContent();
    const plainText = newContentState.getPlainText();

    if (props.limit && plainText.length > props.limit) {
      return;
    }

    if (!equals(convertToRaw(newContentState), convertToRaw(currentContentState))) {
      const newChangeState = getChangeStateFromEditorState(newEditorState);
      emitChange(newChangeState);
    }

    if (forceSelection) {
      setEditorState(EditorState.forceSelection(newEditorState, newEditorState.getSelection()));
    } else {
      setEditorState(newEditorState);
    }
  };

  const getChangeStateFromEditorState = (editorState: EditorState) => {
    return {
      type: 'text',
      data: {
        text: editorState.getCurrentContent().getPlainText(),
        raw: convertToRaw(editorState.getCurrentContent()),
        // @ts-expect-error
        placeholder: props.data?.placeholder,
        // @ts-expect-error
        limit: props.data?.limit,
      },
    };
  };

  const handlePastedText = (pastedText: string): 'handled' | 'not-handled' => {
    if (isNil(pastedText)) {
      return 'not-handled';
    }
    const formattedText = formatWysiwygText(pastedText);
    const currentContent = editorState.getCurrentContent();
    const selection = editorState.getSelection();

    if (!props.limit) {
      const blockMap = getBlockMapFromText(formattedText);
      const newContentState = Modifier.replaceWithFragment(currentContent, selection, blockMap);
      handleChange(EditorState.push(editorState, newContentState, 'insert-fragment'));

      return 'handled';
    }

    const availableCount =
      props.limit -
      currentContent.getPlainText().length +
      (selection.isCollapsed() ? 0 : getTextLengthOfSelectedBlocks(editorState));
    if (formattedText.length > availableCount) {
      const croppedText = formattedText.slice(0, availableCount < 0 ? 0 : availableCount);
      const blockMap = getBlockMapFromText(croppedText);
      const newContentState = Modifier.replaceWithFragment(currentContent, selection, blockMap);
      handleChange(EditorState.push(editorState, newContentState, 'insert-characters'));

      return 'handled';
    }

    return 'not-handled';
  };

  const focus = () => {
    if (isFocused || props.isReadOnly || props.isDisabled) {
      return;
    }

    setIsFocused(true);
    setTimeout(() => {
      refs.editorRef.current?.focus();
    });
  };
  const blur = () => {
    props.onBlur?.();
    setIsFocused(false);
  };

  return {
    refs,
    linkPortalController,
    isFocused,
    isEditorActive,
    editorState,
    handleKeyCommand,
    keyBindingFn,
    blockStyleFn,
    setEditorState,
    setIsEditorActive,
    emitChange,
    getChangeStateFromEditorState,
    handleChange,
    focus,
    blur,
    handlePastedText,
  };
};
